import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

interface DashboardStats {
  pendingPicks?: number;
  lowStockItems?: number;
  routesInProgress?: number;
  ordersToday?: number;
}

interface AlertRow { label: string; count: number; path: string; permission: string; tone: 'critical' | 'warning' | 'info' }

/** Builds the alert rows shown in the header bell from dashboard statistics. */
export function alertRows(stats: DashboardStats | null | undefined): AlertRow[] {
  if (!stats) return [];
  const rows: AlertRow[] = [
    { label: 'Low stock products', count: Number(stats.lowStockItems || 0), path: '/inventory', permission: 'inventory.list', tone: 'warning' },
    { label: 'Orders waiting to be picked', count: Number(stats.pendingPicks || 0), path: '/pick-lists', permission: 'picking.lists.list', tone: 'info' },
    { label: 'Routes in progress', count: Number(stats.routesInProgress || 0), path: '/routes', permission: 'routes.list', tone: 'info' },
  ];
  return rows.filter(row => Number.isFinite(row.count) && row.count > 0);
}

const toneColor: Record<AlertRow['tone'], string> = { critical: '#d72c0d', warning: '#b98900', info: '#2c6ecb' };

/** Header bell: opens a list of things that need attention, each linking to its page. */
export function HeaderAlerts() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { fetchApi } = useApi();
  const canView = hasPermission('dashboard.stats.view');
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    const data = await fetchApi<DashboardStats>('/dashboard/stats');
    if (data) { setStats(data); setFailed(false); } else setFailed(true);
    setLoading(false);
  }, [canView, fetchApi]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (open) void load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const rows = alertRows(stats).filter(row => hasPermission(row.permission));
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const label = total ? `Alerts, ${total} item${total === 1 ? '' : 's'} need attention` : 'Alerts';

  return (
    <div className="user-menu" ref={menuRef}>
      <button type="button" ref={buttonRef} className="header-icon-btn" aria-label={label} title="Alerts"
        aria-expanded={open} aria-controls="alerts-dropdown" aria-haspopup="menu" onClick={() => setOpen(value => !value)}
        style={{ position: 'relative' }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {total > 0 && (
          <span aria-hidden="true" style={{ position: 'absolute', top: 6, right: 6, minWidth: 8, height: 8, borderRadius: 8, background: '#d72c0d' }} />
        )}
      </button>
      {open && (
        <div className="user-dropdown" id="alerts-dropdown" role="menu" style={{ minWidth: 280 }}>
          <div className="user-dropdown-header">
            <div className="user-dropdown-name">Alerts</div>
            <div className="user-dropdown-email">
              {!canView ? 'Dashboard statistics are not available for your role.'
                : failed ? 'Alerts could not be loaded. Try again shortly.'
                : loading && !stats ? 'Loading…'
                : rows.length ? `${total} item${total === 1 ? '' : 's'} need attention` : 'Nothing needs attention right now.'}
            </div>
          </div>
          {rows.length > 0 && (
            <div className="user-dropdown-section">
              {rows.map(row => (
                <a key={row.label} className="user-dropdown-item" role="menuitem" href={row.path}
                  onClick={event => { event.preventDefault(); setOpen(false); navigate(row.path); }}>
                  <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 8, background: toneColor[row.tone], flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{row.label}</span>
                  <span style={{ fontWeight: 600 }}>{row.count}</span>
                </a>
              ))}
            </div>
          )}
          {canView && (
            <div className="user-dropdown-section">
              <a className="user-dropdown-item" role="menuitem" href="/" onClick={event => { event.preventDefault(); setOpen(false); navigate('/'); }}>
                Open dashboard
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
